import React, { useEffect, useMemo, useState } from 'react';
import { TextInput } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import type { ComboSubItem } from '../../../../../utils/orderline-selections';
import classes from './combo-notes-modal.module.scss';

const MAX_NOTE_LENGTH = 120;

export type ComboNotePart = {
  quantity: number;
  note: string | null;
};

export type ComboNoteSelection = {
  selectionIds: number[];
  parts: ComboNotePart[];
};

export type ComboNotesModalProps = {
  isOpen: boolean;
  productName: string;
  comboItems: ComboSubItem[];
  lineQuantity: number;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (selections: ComboNoteSelection[]) => void;
};

// A component split by quantity arrives as sibling selection rows sharing one name; the dialog edits
// them as one component with parts so the waiter never has to think about rows.
type EditablePart = { selectionId: number | null; quantity: number; note: string };
type EditableComponent = {
  key: string;
  name: string;
  totalQuantity: number;
  parts: EditablePart[];
};

const stripQty = (name: string): string => name.replace(/\s*[x×]\s*\d+\s*$/iu, '').trim();

const buildComponents = (comboItems: ComboSubItem[]): EditableComponent[] => {
  const byKey = new Map<string, EditableComponent>();
  comboItems.forEach((item, index) => {
    const name = stripQty(item.name);
    const key = `${item.itemId ?? `i${index}`}::${name}`;
    const part: EditablePart = {
      selectionId: item.selectionId ?? null,
      quantity: Math.max(1, item.quantity),
      note: item.note ?? '',
    };
    const existing = byKey.get(key);
    if (existing) {
      existing.totalQuantity += part.quantity;
      existing.parts.push(part);
      return;
    }
    byKey.set(key, { key, name, totalQuantity: part.quantity, parts: [part] });
  });
  return [...byKey.values()];
};

// comboItems is a fresh array on every render, so its identity cannot gate the state reset.
const signatureOf = (comboItems: ComboSubItem[]): string =>
  comboItems.map((item) => `${item.selectionId ?? 'x'}:${item.quantity}:${item.note ?? ''}`).join('|');

const ComboNotesModalComponent: React.FC<ComboNotesModalProps> = ({
  isOpen,
  productName,
  comboItems,
  lineQuantity,
  isSaving = false,
  onClose,
  onConfirm,
}) => {
  const [components, setComponents] = useState<EditableComponent[]>([]);
  const [splitTarget, setSplitTarget] = useState<string | null>(null);
  const signature = useMemo(() => signatureOf(comboItems), [comboItems]);

  useEffect(() => {
    if (!isOpen) return;
    setComponents(buildComponents(comboItems));
    setSplitTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- comboItems identity changes every render; signature is the real trigger.
  }, [isOpen, signature]);

  if (!isOpen) return null;

  const updatePartNote = (componentKey: string, partIndex: number, note: string) => {
    setComponents((current) => current.map((component) => (
      component.key === componentKey
        ? { ...component, parts: component.parts.map((part, index) => (index === partIndex ? { ...part, note } : part)) }
        : component
    )));
  };

  const splitComponent = (componentKey: string, firstQuantity: number) => {
    setComponents((current) => current.map((component) => {
      if (component.key !== componentKey) return component;
      const [head, ...rest] = component.parts;
      const remainder = head.quantity - firstQuantity;
      if (remainder <= 0) return component;
      return {
        ...component,
        parts: [{ ...head, quantity: firstQuantity }, { selectionId: null, quantity: remainder, note: '' }, ...rest],
      };
    }));
    setSplitTarget(null);
  };

  const mergePart = (componentKey: string, partIndex: number) => {
    setComponents((current) => current.map((component) => {
      if (component.key !== componentKey || component.parts.length <= 1) return component;
      const dropped = component.parts[partIndex];
      const parts = component.parts.filter((_, index) => index !== partIndex);
      parts[0] = { ...parts[0], quantity: parts[0].quantity + dropped.quantity };
      return { ...component, parts };
    }));
  };

  const handleConfirm = () => {
    const selections: ComboNoteSelection[] = components
      .map((component) => ({
        selectionIds: component.parts
          .map((part) => part.selectionId)
          .filter((id): id is number => id != null),
        parts: component.parts.map((part) => ({
          quantity: part.quantity,
          note: part.note.trim().length > 0 ? part.note.trim() : null,
        })),
      }))
      .filter((selection) => selection.selectionIds.length > 0);
    onConfirm(selections);
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label="הערות לפריטי הקומבו">
      <div className={classes.header}>
        <h2 className={classes.title}>הערות לפריטי הקומבו</h2>
        <span className={classes.subtitle}>{productName}</span>
      </div>

      <div className={classes.body}>
        <div className={classes.sheet}>
          {components.length === 0 && (
            <div className={classes.card}>
              <span className={classes.emptyText}>לא נמצאו פריטי קומבו להערה.</span>
            </div>
          )}

          {components.map((component) => {
            const canSplit = component.parts.length === 1 && component.parts[0].quantity > 1;
            return (
              <div key={component.key} className={classes.card}>
                <div className={classes.componentHeader}>
                  <span className={classes.componentName}>
                    {component.name} ×{component.totalQuantity * Math.max(1, lineQuantity)}
                  </span>
                  {canSplit && (
                    <Button
                      variant="cancel"
                      size="sm"
                      onClick={() => setSplitTarget(splitTarget === component.key ? null : component.key)}
                    >
                      פצל
                    </Button>
                  )}
                </div>

                {splitTarget === component.key && (
                  <div className={classes.splitChips}>
                    <span className={classes.splitChipsLabel}>כמה יחידות בקבוצה הראשונה?</span>
                    <div className={classes.splitChipsRow}>
                      {Array.from({ length: component.parts[0].quantity - 1 }, (_, index) => index + 1).map((quantity) => (
                        <Button key={quantity} variant="confirm" size="sm" onClick={() => splitComponent(component.key, quantity)}>
                          {String(quantity)}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {component.parts.map((part, partIndex) => (
                  <div key={`${component.key}-${partIndex}`} className={classes.partRow}>
                    {component.parts.length > 1 && (
                      <div className={classes.partHeader}>
                        <span className={classes.partQuantity}>
                          {component.name} ×{part.quantity * Math.max(1, lineQuantity)}
                        </span>
                        <Button variant="cancel" size="sm" onClick={() => mergePart(component.key, partIndex)}>
                          בטל פיצול
                        </Button>
                      </div>
                    )}
                    <TextInput
                      classNames={{ input: classes.input }}
                      value={part.note}
                      maxLength={MAX_NOTE_LENGTH}
                      placeholder="הערה לפריט זה"
                      onChange={(event) => updatePartNote(component.key, partIndex, event.currentTarget.value)}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.footerInner}>
          <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving}>
            ביטול
          </Button>
          <Button variant="confirm" fullWidth onClick={handleConfirm} disabled={isSaving}>
            {isSaving ? 'שומר...' : 'אישור'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ComboNotesModal = React.memo(ComboNotesModalComponent);
